// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useEffect, useCallback, useReducer, useRef } from 'react';

// Spread onto a <div>/<span> standing in for a real button (a clickable card,
// row, or dropzone) — adds the role/keyboard semantics a native <button> gets
// for free, without changing its existing onClick. Root fix for the whole
// "static element has an interaction" class instead of hand-rolling
// role/tabIndex/onKeyDown at each site.
export const clickableDivProps = (onClick) => ({
  role: 'button',
  tabIndex: 0,
  onClick,
  onKeyDown: (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
  },
});
import { focusPageSearch } from './quickFind';
import { isDomainLoaded, loadDomain, WRITE_EVENT } from './backOfficeApi';
import { COMPACT_WINDOW_IDS } from './dateRange';
import { BroadcastComposerModal } from '../components/composers/BroadcastComposerModal';
import { NoteComposerModal } from '../components/composers/NoteComposerModal';
import { PermissionEditorModal } from '../components/composers/PermissionEditorModal';
import { PolicyEditorModal } from '../components/composers/PolicyEditorModal';
import { toast } from './buses';
import { ExportFlowModal } from '../modules/sales/ExportFlowModal';
import { ConfirmDialog } from '../ui/Modal';
import { Button } from '../ui/Button';

// Lazily hydrate a backend domain when a page mounts, instead of fetching the
// whole platform on startup. Pages keep reading their MOCK singleton; this hook
// fetches the domain once (cached for the session) and forces a re-render when
// the data lands. Returns { loading, error, refresh }.
/**
 * A window/date-range choice that SURVIVES RELOAD.
 *
 * Every strip that had a window switcher held it in plain useState, so a reload silently
 * reset the operator to the hardcoded default — on a console people keep open all day and
 * refresh constantly. This is one hook rather than four copies of the same localStorage
 * dance, so a new switcher gets persistence by using it, not by remembering to add it.
 *
 * Keyed per surface (`orders`, `transactions`, …) so the Orders strip and the Transactions
 * strip remember independently — one operator's "today on Orders, 90d on Transactions" is a
 * real working state, not a conflict to resolve.
 *
 * Reads are validated against the CURRENT preset list: if a stored id is later removed from
 * lib/dateRange, the fallback wins instead of resolving to an empty window forever. Storage
 * is wrapped because Safari private mode throws on setItem — a failed write must not take
 * the page down, it just means the choice doesn't outlive the session.
 */
/**
 * A REMEMBERED CURRENCY, sibling to useWindowPreference above.
 *
 * The two controls sit on the same strip and scope the same page, so it read as arbitrary that one
 * outlived a reload and the other did not: pick KES, refresh, and you are looking at UGX with no
 * indication anything changed.
 *
 * Deliberately NOT validated here, unlike the window's preset check — which currencies exist is a
 * property of the EVENT and the window currently selected, and this hook knows neither. The caller
 * validates against the currencies that actually have revenue, and falls back to the dominant one.
 * Storing a currency this event has never seen is harmless; resolving to it would not be.
 *
 * Accepts an updater function, because the caller's decision depends on the previous choice
 * ("keep it if it is still available").
 */
export const useCurrencyPreference = (key) => {
  const storageKey = `tkt.currency.${key}`;
  const [value, setValue] = useState(() => {
    try { return window.localStorage.getItem(storageKey) || null; } catch { return null; }
  });
  const set = useCallback((next) => {
    setValue((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      // Written inside the updater so the stored value can never disagree with the rendered one.
      try {
        if (resolved) window.localStorage.setItem(storageKey, resolved);
        else window.localStorage.removeItem(storageKey);
      } catch { /* private mode — session-only */ }
      return resolved;
    });
  }, [storageKey]);
  return [value, set];
};

// "ALL" IS NEVER REMEMBERED. It reads every order ever taken (finance, all time: 421 ms against 12.6 ms for 30 days),
// so it is a choice for the view in front of you, never the next visit's default: picking it once used to make it the
// page's opening read from then on. A page whose own default is 'all' (an event's summary — bounded by that event)
// keeps it.
export const useWindowPreference = (key, fallback = '30d') => {
  const storageKey = `tkt.window.${key}`;
  const remembers = (w) => w && COMPACT_WINDOW_IDS.includes(w) && (w !== 'all' || fallback === 'all');
  const [value, setValue] = useState(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      return remembers(stored) ? stored : fallback;
    } catch { return fallback; }
  });
  const set = useCallback((next) => {
    setValue(next);
    try {
      if (remembers(next)) window.localStorage.setItem(storageKey, next);
      else window.localStorage.removeItem(storageKey);
    } catch { /* private mode — session-only */ }
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return [value, set];
};

/**
 * A ref that always holds the LATEST value — for a long-lived callback/effect (a WebSocket
 * handler, an outside-click listener) that needs to read a current prop/value without
 * re-subscribing every render. `ref.current = value` written directly in the render body (the
 * pattern this replaces — found independently reinvented in 5 files) is a side effect during
 * render: React does not guarantee a render body only runs once (Strict Mode double-invokes it
 * on purpose to catch exactly this class of bug). Assigning inside an effect makes it a real,
 * once-per-commit side effect instead.
 */
// A LIST A PERSON READS: one page (LIST_PAGE_SIZE rows), and "Load more" for the next. `load({ page, last, signal })`
// returns { rows, hasMore } — `page` for a paged route, `last` (the last row shown) for a cursor one. It reloads from
// page 1 whenever `deps` change. Render <LoadMore list={…} /> under the rows: it shows only while there are more.
// `meta` is whatever page 1 says about the WHOLE list (totals, counts) — kept as page 1 said it.
export const usePagedRows = (load, deps) => {
  const [state, setState] = useState({ rows: [], hasMore: false, meta: null, page: 0, loading: true, loadingMore: false, error: null });
  const loadRef = useLatestRef(load);
  useEffect(() => {
    const ac = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    loadRef.current({ page: 1, last: null, signal: ac.signal })
      .then((r) => { if (!ac.signal.aborted) setState({ rows: r?.rows || [], hasMore: !!r?.hasMore, meta: r?.meta ?? null, page: 1, loading: false, loadingMore: false, error: null }); })
      .catch((e) => { if (!ac.signal.aborted) setState((s) => ({ ...s, loading: false, error: e?.message || 'Could not load' })); });
    return () => ac.abort();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  const stateRef = useLatestRef(state);
  const busy = useRef(false); // a second click before the button re-renders disabled must not fetch the page twice
  const loadMore = useCallback(() => {
    const s = stateRef.current;
    if (busy.current || !s.hasMore) return;
    busy.current = true;
    setState((t) => ({ ...t, loadingMore: true }));
    loadRef.current({ page: s.page + 1, last: s.rows[s.rows.length - 1] ?? null })
      .then((r) => setState((t) => ({ ...t, rows: [...t.rows, ...(r?.rows || [])], hasMore: !!r?.hasMore, page: t.page + 1, loadingMore: false })))
      .catch((e) => setState((t) => ({ ...t, loadingMore: false, error: e?.message || 'Could not load more' })))
      .finally(() => { busy.current = false; });
  }, [loadRef, stateRef]);
  return { ...state, loadMore, setRows: (f) => setState((s) => ({ ...s, rows: typeof f === 'function' ? f(s.rows) : f })) };
};

// A COUNTER THAT MOVES AFTER ANY WRITE IN THE CONSOLE, settled for 800 ms — put it in a history list's deps and the
// change just made is there when it's done (the audit log records an act after its response, within 250 ms).
export const useWriteTick = () => {
  const [writes, setWrites] = useState(0);
  useEffect(() => {
    let t = null;
    const onWrite = () => { clearTimeout(t); t = setTimeout(() => setWrites((n) => n + 1), 800); };
    window.addEventListener(WRITE_EVENT, onWrite);
    return () => { clearTimeout(t); window.removeEventListener(WRITE_EVENT, onWrite); };
  }, []);
  return writes;
};

export const LoadMore = ({ list, className = 'flex justify-center py-3' }) => (list?.hasMore ? (
  <div className={className}>
    <Button variant="ghost" size="sm" disabled={list.loadingMore} onClick={list.loadMore}>{list.loadingMore ? 'Loading…' : 'Load more'}</Button>
  </div>
) : null);

export const useLatestRef = (value) => {
  const ref = useRef(value);
  useEffect(() => { ref.current = value; });
  return ref;
};

/**
 * A SET of ids "pinned" IN THIS BROWSER — same idiom as useWindowPreference above (one hook,
 * `tkt.<namespace>` key, wrapped for Safari private mode), just a Set instead of a single value.
 * Genuinely per-browser: no backend row, no shared-across-operators meaning — if a future pin
 * ever needs to mean "pinned for the whole team," that's a different feature, not this one.
 */
export const usePinnedSet = (key) => {
  const storageKey = `tkt.pinned.${key}`;
  const [pinned, setPinned] = useState(() => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      return new Set(Array.isArray(stored) ? stored : []);
    } catch { return new Set(); }
  });
  // The write belongs OUTSIDE the state updater — React may invoke an updater function more than
  // once per commit (Strict Mode's double-invoke is exactly this check), so a side effect inside
  // one isn't guaranteed to run once. Syncing via effect keeps `toggle`'s updater pure and still
  // persists every real change, including one made by a future caller other than `toggle`.
  useEffect(() => {
    try { window.localStorage.setItem(storageKey, JSON.stringify([...pinned])); } catch { /* private mode — session-only */ }
  }, [storageKey, pinned]);
  const toggle = useCallback((id) => {
    setPinned((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  return [pinned, toggle];
};

export const useDomain = (key) => {
  const [, force] = useReducer((x) => x + 1, 0);
  const [loading, setLoading] = useState(() => !isDomainLoaded(key));
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (isDomainLoaded(key)) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    loadDomain(key)
      .then(() => { if (active) { setLoading(false); force(); } })
      .catch((err) => { if (active) { setLoading(false); setError(err?.message || 'Failed to load'); } });
    return () => { active = false; };
  }, [key]);

  const refresh = useCallback(
    () => loadDomain(key, { force: true }).then(() => force()).catch((err) => setError(err?.message || 'Failed to load')),
    [key],
  );

  return { loading, error, refresh };
};

export const useKeyboardShortcuts = ({ setActive, setPaletteOpen, setHelpOpen, openPaletteAction }) => {
  useEffect(() => {
    let chord = null;        // first key in a g-then-X / n-then-X combo
    let chordTimer = null;

    const clearChord = () => { chord = null; if (chordTimer) { clearTimeout(chordTimer); chordTimer = null; } };

    const handler = (e) => {
      // Ignore when typing in inputs/textareas/contenteditable
      const target = e.target;
      const tag = target && target.tagName;
      // TWO DIFFERENT REASONS TO STAND ASIDE, and they don't cover the same keys.
      //
      // These were one flag, and `<select>` was in it. The Events page's filter row is five
      // selects — organizer, venue, category, publish state, date — so touching any filter
      // left focus on a select and silently killed `/` for the rest of the visit. Nothing
      // was typing; the shortcut just refused to fire, which reads as "the key is broken".
      //
      //   TEXT ENTRY — the keystroke is literally becoming content. Every shortcut yields.
      //   TYPE-AHEAD — a focused <select> jumps to the option starting with the letter you
      //     press. That's real, so LETTER chords (g e, n o) still yield to it — but `/` and
      //     `?` are not select type-ahead in any list here, and stealing them costs nothing.
      const isTextEntry = tag === 'TEXTAREA'
        || (target && target.isContentEditable)
        || (tag === 'INPUT' && !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'].includes(String(target.type || 'text').toLowerCase()));
      const isTypeAhead = tag === 'SELECT';
      const inField = isTextEntry || isTypeAhead;

      // ⌘K / Ctrl+K → open palette (works even in fields)
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen(true);
        clearChord();
        return;
      }
      // ⌘/ → open palette (alt)
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setPaletteOpen(true);
        clearChord();
        return;
      }

      // `/` and `?` only yield to genuine text entry — a focused filter dropdown is not
      // someone typing, and it used to swallow both.
      if (isTextEntry) return;

      // / → put the cursor in THIS page's search box (GitHub/Gmail idiom).
      //
      // Deliberately not a new global search: every list page already owns a search input
      // wired to its own real server-side filter, and none of them was reachable from the
      // keyboard. See lib/quickFind — it focuses the box the operator would have clicked,
      // so the semantics are always that page's own search, never a second definition.
      //
      // Escalates to the command palette when the page has nothing to search, rather than
      // being a key that silently does nothing on half the console.
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        if (!focusPageSearch()) setPaletteOpen(true);
        return;
      }

      // ? → help
      if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }

      // From here down every shortcut is a LETTER, which is exactly what a focused
      // <select> uses to jump between options — so these still stand aside for it.
      if (isTypeAhead) return;

      // First key of a chord
      if (!chord && (e.key === 'g' || e.key === 'n')) {
        chord = e.key;
        chordTimer = setTimeout(clearChord, 900);
        return;
      }

      // Second key of a chord
      if (chord === 'g') {
        const map = { e: 'events', o: 'orders', t: 'transactions', p: 'payouts', r: 'risk', a: 'access-control', h: 'dashboard', s: 'sales-setup', c: 'channels', v: 'venues', u: 'people' };
        if (map[e.key]) {
          e.preventDefault();
          setActive(map[e.key]);
          toast(`→ ${map[e.key]}`, 'info', `g ${e.key} shortcut`);
        }
        clearChord();
        return;
      }
      if (chord === 'n') {
        // `n` chords CREATE something. `n r` (new risk rule) and `n p` (initiate payout)
        // did not: one dispatched an event at a page that is no longer in the sidebar, the
        // other navigated and toasted "Use the page action" — a shortcut whose payload is
        // instructions for finding the real control. Both removed along with their palette
        // entries. `n o` is new: it opens the manual-order wizard from anywhere, which is
        // the only `n` the palette ever advertised (`keys: 'n o'`) without it being wired.
        const newActions = {
          o: () => openPaletteAction && openPaletteAction('new-manual-order'),
          e: () => { setActive('event-requests'); toast('Tip', 'info', 'Open a request to approve into an event'); },
        };
        if (newActions[e.key]) {
          e.preventDefault();
          newActions[e.key]();
        }
        clearChord();
      }
    };
    document.addEventListener('keydown', handler);
    return () => { document.removeEventListener('keydown', handler); if (chordTimer) clearTimeout(chordTimer); };
  }, [setActive, setPaletteOpen, setHelpOpen, openPaletteAction]);
};


export const useConfirm = () => {
  const [state, setState] = useState({ open: false });
  const close = () => setState({ open: false });
  const confirm = (opts) => setState({ open: true, ...opts });
  const node = <ConfirmDialog {...state} onClose={close} />;
  return { confirm, node };
};

/* ============================================================================
   REUSABLE: Note composer + Broadcast composer (replaces toast placeholders)
   ============================================================================ */


export const useNoteComposer = () => {
  const [state, setState] = useState({ open: false });
  const open = (opts = {}) => setState({ open: true, ...opts });
  const close = () => setState({ open: false });
  const node = <NoteComposerModal {...state} onClose={close} />;
  return { openNote: open, noteNode: node };
};


export const useBroadcast = () => {
  const [state, setState] = useState({ open: false });
  const openBroadcast = (opts = {}) => setState({ open: true, ...opts });
  const close = () => setState({ open: false });
  const broadcastNode = <BroadcastComposerModal {...state} onClose={close} />;
  return { openBroadcast, broadcastNode };
};

/* ============================================================================
   REUSABLE: PolicyEditor / Reassign / PermissionEditor (replaces toast stubs)
   ============================================================================ */

// Generic policy editor — handles refund rules, financial rules, settlement, entitlements

export const usePolicyEditor = () => {
  const [state, setState] = useState({ open: false });
  const openPolicy = (opts = {}) => setState({ open: true, ...opts });
  const close = () => setState({ open: false });
  const policyNode = <PolicyEditorModal {...state} onClose={close} />;
  return { openPolicy, policyNode };
};

// useReassign / ReassignModal removed — its only consumer (OrderDetailPage) destructured
// `openReassign` and never called it, so the modal was unreachable; and because no caller
// passed `candidates`, its fallback would have offered five invented staff members as the
// people to reassign a real order to. Needs a real team roster to come back.

// Permission editor — toggle list of permissions for a role

export const usePermissionEditor = () => {
  const [state, setState] = useState({ open: false });
  const openPermissions = (opts = {}) => setState({ open: true, ...opts });
  const close = () => setState({ open: false });
  const permNode = <PermissionEditorModal {...state} onClose={close} />;
  return { openPermissions, permNode };
};

// Dropdown menu — for "···" / "More actions"

export const useExport = ({ datasetLabel, totalRows, columns }) => {
  const [isOpen, setOpen] = useState(false);
  const open = () => setOpen(true);
  const node = isOpen ? <ExportFlowModal onClose={() => setOpen(false)} datasetLabel={datasetLabel} totalRows={totalRows} presetColumns={columns} /> : null;
  return { open, node };
};

/* ----------------------------------------------------------------------------
   ONBOARD ORGANIZER WIZARD
   ---------------------------------------------------------------------------- */

